import type { ParsedUrlQuery } from 'querystring'

import type {
    BrowseFilters,
    ColorFamilySlug,
    DeviceFilter,
    SortOption
} from '@/types'

import { isColorFamily } from '@/lib/colors'

export const DEFAULT_FILTERS: BrowseFilters = {
    q: '',
    category: '',
    device: 'all',
    color: '',
    sort: 'latest',
    page: 1
}

export const SORT_OPTIONS: Array<{ value: SortOption, label: string }> = [
    { value: 'latest', label: 'Latest' },
    { value: 'popular', label: 'Most downloaded' },
    { value: 'title', label: 'A to Z' }
]

export const DEVICE_OPTIONS: Array<{ value: DeviceFilter, label: string }> = [
    { value: 'all', label: 'All devices' },
    { value: 'desktop', label: 'Desktop' },
    { value: 'phone', label: 'Phone' }
]

const first = (value: string | Array<string> | undefined): string => {
    if (Array.isArray(value)) return value[0] ?? ''

    return value ?? ''
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

// Reads the browse filters from the URL, ignoring anything that is not a known value.
export const parseBrowseFilters = (query: ParsedUrlQuery): BrowseFilters => {
    const q = first(query.q).trim().slice(0, 100)
    const category = first(query.category).trim().toLowerCase()
    const device = first(query.device)
    const color = first(query.color)
    const sort = first(query.sort)
    const page = Number.parseInt(first(query.page), 10)

    return {
        q,
        category: SLUG_PATTERN.test(category) ? category : '',
        device: device === 'desktop' || device === 'phone' ? device : 'all',
        color: isColorFamily(color) ? color as ColorFamilySlug : '',
        sort: sort === 'popular' || sort === 'title' ? sort : 'latest',
        page: Number.isFinite(page) && page > 1 ? Math.min(page, 500) : 1
    }
}

// Builds a /wallpapers link from the current filters plus any changes, leaving defaults out of the URL.
// Changing any filter other than the page sends the visitor back to page one.
export const buildBrowseHref = (filters: BrowseFilters, changes: Partial<BrowseFilters> = {}): string => {
    const next: BrowseFilters = { ...filters, ...changes }

    if (!('page' in changes)) next.page = 1

    const params = new URLSearchParams()

    if (next.q) params.set('q', next.q)
    if (next.category) params.set('category', next.category)
    if (next.device !== 'all') params.set('device', next.device)
    if (next.color) params.set('color', next.color)
    if (next.sort !== 'latest') params.set('sort', next.sort)
    if (next.page > 1) params.set('page', String(next.page))

    const search = params.toString()

    return search ? `/wallpapers?${search}` : '/wallpapers'
}

export const hasActiveFilters = (filters: BrowseFilters): boolean =>
    Boolean(filters.q || filters.category || filters.color || filters.device !== 'all')
